import { useState, type FormEvent } from 'react';
import type { ChatToolName } from '@fantasy/types';
import { useSquad } from '../state/useSquad.ts';

/** Human labels for the tools `ChatResponse.toolsUsed` can name — shown so
 * the manager can see a reply drew on the same arithmetic as the verdict,
 * not a second opinion invented in prose. */
const TOOL_LABELS: Record<ChatToolName, string> = {
  score_player: 'player score',
  plan_transfers: 'transfer planner',
  check_transfer: 'transfer check',
};

/** Offered on an empty conversation — a blank box right under a verdict the
 * manager may disagree with is a hard place to start typing from. Clicking
 * one populates the input rather than sending immediately, so it reads as a
 * suggestion, not a shortcut that skips the manager's own words. */
const STARTER_PROMPTS = [
  'Why not a differential captain?',
  'What if I take a hit?',
  'Why hold instead of transferring?',
];

/**
 * Argue with the verdict (T-064): a conversation under `AnalysisView`, so a
 * manager who disagrees with the captain call or a transfer has somewhere to
 * push back rather than just re-running the analysis. The conversation
 * itself lives in `SquadProvider`, not here — this component only renders it
 * and drives one turn at a time through `sendChatMessage`. Reads `squad` and
 * `analysis` straight off the context rather than taking them as props, so
 * it can gate its own "nothing to argue with yet" case instead of leaning on
 * the caller to get that right.
 */
export function AnalysisChat() {
  const { squad, analysis, chatMessages, chatPending, chatProblem, sendChatMessage } = useSquad();
  const [value, setValue] = useState('');

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!squad) return;
    const message = value.trim();
    if (!message || chatPending) return;

    // Only cleared on success — a failure leaves what was typed in place so
    // nothing the manager wrote is lost.
    const sent = await sendChatMessage(squad.id, message);
    if (sent) setValue('');
  }

  // There is nothing to argue with before a verdict exists.
  if (!squad || !analysis) return null;

  return (
    <section className="panel chat-panel" aria-labelledby="chat-heading">
      <h3 id="chat-heading">Argue with the verdict</h3>
      <p className="hint">Push back on the captain call, a transfer, or ask what a hit would cost.</p>

      {chatMessages.length === 0 ? (
        <div className="chat-starters">
          {STARTER_PROMPTS.map((prompt) => (
            <button key={prompt} type="button" className="ghost small" onClick={() => setValue(prompt)}>
              {prompt}
            </button>
          ))}
        </div>
      ) : (
        // An <ol> since turns genuinely have an order — each entry states its
        // own author as real text ("You"/"Assistant"), not just alignment or
        // colour, so the sequence reads the same way to a screen reader as
        // it does visually.
        <ol className="chat-log list-reset" aria-label="Conversation">
          {chatMessages.map((entry, index) => (
            <li key={`${entry.role}-${index}`} className={`chat-message chat-message--${entry.role}`}>
              <span className="chat-author">{entry.role === 'user' ? 'You' : 'Assistant'}</span>
              <p>{entry.content}</p>
              {entry.toolsUsed && entry.toolsUsed.length > 0 && (
                <p className="hint chat-tools-used">
                  Checked: {entry.toolsUsed.map((tool) => TOOL_LABELS[tool]).join(', ')}
                </p>
              )}
            </li>
          ))}
        </ol>
      )}

      {chatProblem && (
        <ul className="notices mt-md">
          <li className="error">{chatProblem}</li>
        </ul>
      )}

      <form className="chat-form mt-md" onSubmit={handleSubmit}>
        <label htmlFor="chat-message">Ask a question</label>
        <div className="row row--flush">
          <input
            id="chat-message"
            type="text"
            className="field--grow"
            value={value}
            onChange={(event) => setValue(event.target.value)}
            disabled={chatPending}
          />
          <button type="submit" className="small" disabled={chatPending || value.trim().length === 0} aria-busy={chatPending}>
            {chatPending && <span className="spinner" aria-hidden="true" />}
            {chatPending ? 'Sending…' : 'Send'}
          </button>
        </div>
      </form>
    </section>
  );
}
